import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  Paper,
} from '@mui/material';
import {
  CameraIcon,
  DocumentArrowUpIcon,
  XMarkIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import Modal from '../Modal';
import { useTheme } from '../../context/ThemeContext';
import { AppIconButton, PrimaryButton, SecondaryButton } from '../ui';
import { todayLocalIso } from '../../utils/dateLocal';
import { ocrApi } from '../../api/ocrApi';

// --- Types ---

export interface OcrResultData {
  supplierName?: string;
  date?: string;
  totalAmount?: number;
  subtotal?: number;
  cgst?: number;
  sgst?: number;
  igst?: number;
  lineItems?: Array<{
    description: string;
    quantity?: number;
    rate?: number;
    amount?: number;
  }>;
  confidence?: number;
  parser?: string;
  rawText?: string;
  imageDataUrl?: string;
}

interface InvoiceScannerProps {
  open: boolean;
  onClose: () => void;
  onResult: (ocrData: OcrResultData) => void;
}

// --- Step definitions ---

interface ProcessingStep {
  key: string;
  label: string;
  status: 'pending' | 'active' | 'done' | 'error';
  detail?: string;
}

const INITIAL_STEPS: ProcessingStep[] = [
  { key: 'preprocess', label: 'Preprocessing image...', status: 'pending' },
  { key: 'extract', label: 'Extracting text...', status: 'pending' },
  { key: 'analyze', label: 'Analyzing invoice...', status: 'pending' },
];

// --- Component ---

const InvoiceScanner: React.FC<InvoiceScannerProps> = ({ open, onClose, onResult }) => {
  const { theme } = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [steps, setSteps] = useState<ProcessingStep[]>(INITIAL_STEPS);
  const [error, setError] = useState<string | null>(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraAvailable, setCameraAvailable] = useState(true);

  const resetState = useCallback(() => {
    setSelectedImage(null);
    setProcessing(false);
    setSteps(INITIAL_STEPS);
    setError(null);
    setCameraActive(false);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  }, []);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [onClose, resetState]);

  // --- Camera ---

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setCameraActive(false);
  }, []);

  const startCamera = useCallback(async () => {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } },
      });
      streamRef.current = stream;
      // Set active first so the <video> element renders, then attach stream in useEffect
      setCameraActive(true);
    } catch (err) {
      console.error('Camera access denied:', err);
      setCameraAvailable(false);
      setError('Camera not available. Please upload an image instead.');
    }
  }, []);

  const capturePhoto = useCallback(() => {
    if (!videoRef.current || !streamRef.current) return;
    const video = videoRef.current;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.drawImage(video, 0, 0);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
      setSelectedImage(dataUrl);
      stopCamera();
    }
  }, [stopCamera]);

  // Attach stream to video element once it renders
  useEffect(() => {
    if (cameraActive && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play().catch(() => {});
    }
  }, [cameraActive]);

  // Cleanup camera on close/unmount
  useEffect(() => {
    return () => { stopCamera(); };
  }, [stopCamera]);

  // --- Image selection ---

  const handleImageSelected = useCallback((file: File) => {
    setError(null);

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file (JPG, PNG, etc.)');
      return;
    }

    // Validate file size (max 15MB)
    if (file.size > 15 * 1024 * 1024) {
      setError('Image is too large. Please select an image under 15MB.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setSelectedImage(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  }, []);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) handleImageSelected(file);
      // Reset input so same file can be re-selected
      e.target.value = '';
    },
    [handleImageSelected]
  );

  // --- Step updater ---

  const updateStep = useCallback(
    (key: string, status: ProcessingStep['status'], detail?: string) => {
      setSteps((prev) =>
        prev.map((s) => (s.key === key ? { ...s, status, detail } : s))
      );
    },
    []
  );

  // --- Processing pipeline ---

  const processImage = useCallback(async () => {
    if (!selectedImage) return;

    setProcessing(true);
    setError(null);
    setSteps(INITIAL_STEPS);

    try {
      // Step 1: Preprocess with OpenCV (lazy loaded)
      updateStep('preprocess', 'active');
      let processedCanvas: HTMLCanvasElement;

      try {
        const { loadOpenCV, preprocessImage } = await import('../../lib/ocrPreprocess');
        await loadOpenCV();

        // Create an image element from the data URL
        const img = await createImageElement(selectedImage);
        processedCanvas = preprocessImage(img);
        updateStep('preprocess', 'done');
      } catch (preprocessError) {
        console.warn('Preprocessing skipped:', preprocessError);
        // Create a canvas from the original image as fallback
        const img = await createImageElement(selectedImage);
        processedCanvas = document.createElement('canvas');
        processedCanvas.width = img.naturalWidth || img.width;
        processedCanvas.height = img.naturalHeight || img.height;
        const ctx = processedCanvas.getContext('2d');
        if (ctx) ctx.drawImage(img, 0, 0);
        updateStep('preprocess', 'done', 'Skipped (OpenCV unavailable)');
      }

      // Step 2: Extract structured text with Tesseract.js
      updateStep('extract', 'active');
      const { extractStructuredText } = await import('../../lib/ocrExtract');
      const ocrResult = await extractStructuredText(processedCanvas);

      if (ocrResult.error) {
        throw new Error(ocrResult.error);
      }
      updateStep('extract', 'done', `${ocrResult.structuredText.length} words, ${Math.round(ocrResult.pageConfidence)}% confidence`);

      // Step 3: Send to backend for parsing
      updateStep('analyze', 'active');

      const formData = new FormData();
      // Send the structured text as JSON for the Java parser
      formData.append(
        'structuredText',
        JSON.stringify({
          words: ocrResult.structuredText,
          rawText: ocrResult.rawText,
          pageConfidence: ocrResult.pageConfidence,
        })
      );

      // Also send the original image file for storage/fallback
      const blob = dataURLtoBlob(selectedImage);
      formData.append('file', blob, 'invoice-scan.jpg');

      const response = await ocrApi.parseInvoice(formData);
      const parsed = response.data;
      updateStep('analyze', 'done', parsed.parser ? `Parser: ${parsed.parser}` : undefined);

      // Pass result to parent with the image for review
      onResult({
        supplierName: parsed.supplierName || '',
        date: parsed.date || todayLocalIso(),
        totalAmount: parsed.totalAmount || 0,
        subtotal: parsed.subtotal || 0,
        cgst: parsed.cgst || 0,
        sgst: parsed.sgst || 0,
        igst: parsed.igst || 0,
        lineItems: parsed.lineItems || [],
        confidence: parsed.confidence || ocrResult.pageConfidence,
        parser: parsed.parser || 'unknown',
        rawText: ocrResult.rawText,
        imageDataUrl: selectedImage,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Processing failed';
      setError(message);

      // Mark the currently active step as error
      setSteps((prev) =>
        prev.map((s) => (s.status === 'active' ? { ...s, status: 'error' } : s))
      );
    } finally {
      setProcessing(false);
    }
  }, [selectedImage, updateStep, onResult]);

  // --- Render helpers ---

  const getStepIcon = (status: ProcessingStep['status']) => {
    switch (status) {
      case 'done':
        return <CheckCircleIcon style={{ width: 22, height: 22, color: theme.success || '#10b981' }} />;
      case 'error':
        return <ExclamationCircleIcon style={{ width: 22, height: 22, color: theme.danger || '#ef4444' }} />;
      case 'active':
        return (
          <ArrowPathIcon
            style={{ width: 22, height: 22, color: theme.primary }}
            className="animate-spin"
          />
        );
      default:
        return (
          <Box
            sx={{
              width: 22,
              height: 22,
              borderRadius: '50%',
              border: `2px solid ${theme.gray?.[300] || '#D0D0D0'}`,
            }}
          />
        );
    }
  };

  return (
    <Modal
      isOpen={open}
      onClose={handleClose}
      title="Scan Invoice"
      maxWidth="sm"
    >
      <Box sx={{ pb: 1 }}>
        {/* Capture Section */}
        {!selectedImage && !processing && (
          <Box sx={{ py: { xs: 2, sm: 3 } }}>

            {/* Live camera view */}
            {cameraActive && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <Box sx={{
                  position: 'relative',
                  width: '100%',
                  maxWidth: 480,
                  borderRadius: '12px',
                  overflow: 'hidden',
                  border: `2px solid ${theme.gray?.[300] || '#D0D0D0'}`,
                }}>
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    style={{ width: '100%', display: 'block', borderRadius: 10 }}
                  />
                  {/* Capture overlay hint */}
                  <Box sx={{
                    position: 'absolute', bottom: 0, left: 0, right: 0,
                    background: 'linear-gradient(transparent, rgba(0,0,0,0.4))',
                    p: 1.5, textAlign: 'center',
                  }}>
                    <Typography sx={{ color: 'var(--text-inverse)', fontSize: '0.8rem', fontWeight: 500 }}>
                      Position the invoice within the frame
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <button
                    onClick={capturePhoto}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                      padding: '12px 32px', borderRadius: 12, border: 'none',
                      backgroundColor: theme.button?.primary?.bg || 'var(--action)', color: 'var(--text-inverse)',
                      fontWeight: 600, fontSize: '0.95rem', cursor: 'pointer', fontFamily: 'inherit',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    }}
                  >
                    <CameraIcon style={{ width: 20, height: 20 }} />
                    Capture
                  </button>
                  <button
                    onClick={stopCamera}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      padding: '12px 20px', borderRadius: 12,
                      border: '1.5px solid #C8C5BD', backgroundColor: 'var(--bg-surface)', /* basil-todo */
                      color: 'var(--text-primary)', fontWeight: 600, fontSize: '0.95rem',
                      cursor: 'pointer', fontFamily: 'inherit',
                    }}
                  >
                    Cancel
                  </button>
                </Box>
              </Box>
            )}

            {/* Buttons — show when camera is NOT active */}
            {!cameraActive && (
              <Box sx={{ textAlign: 'center' }}>
                <Typography
                  variant="body2"
                  sx={{ color: theme.gray?.[600] || '#888', mb: 3, px: 2 }}
                >
                  Take a photo of your invoice or upload an image to automatically extract expense details.
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, maxWidth: 320, mx: 'auto' }}>
                  {/* Take Photo — opens live camera */}
                  {cameraAvailable && (
                    <button
                      onClick={startCamera}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                        padding: '14px 24px', borderRadius: 12, border: 'none',
                        backgroundColor: theme.button?.primary?.bg || 'var(--action)', color: 'var(--text-inverse)',
                        fontWeight: 600, fontSize: '1rem', cursor: 'pointer', width: '100%',
                        fontFamily: 'inherit', boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                      }}
                    >
                      <CameraIcon style={{ width: 22, height: 22 }} />
                      Take Photo
                    </button>
                  )}

                  {cameraAvailable && (
                    <Typography variant="caption" sx={{ color: theme.gray?.[400] || '#AAA' }}>or</Typography>
                  )}

                  {/* Upload file */}
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                      padding: '12px 24px', borderRadius: 12,
                      border: '1.5px solid #C8C5BD', backgroundColor: 'var(--bg-surface)', /* basil-todo */
                      color: 'var(--text-primary)', fontWeight: 600, fontSize: '0.9rem',
                      cursor: 'pointer', width: '100%', fontFamily: 'inherit',
                    }}
                  >
                    <DocumentArrowUpIcon style={{ width: 20, height: 20 }} />
                    Upload Image
                  </button>

                  <Typography variant="caption" sx={{ color: theme.gray?.[500] || '#999', textAlign: 'center' }}>
                    Supports JPEG, PNG, PDF up to 10MB
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,application/pdf"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
          </Box>
        )}

        {/* Image Preview + Processing */}
        {selectedImage && (
          <Box>
            {/* Image preview */}
            <Box
              sx={{
                position: 'relative',
                mb: 2.5,
                borderRadius: '12px',
                overflow: 'hidden',
                border: `1px solid ${theme.components?.card?.border || '#E0E0E0'}`,
                maxHeight: 240,
              }}
            >
              <img
                src={selectedImage}
                alt="Invoice preview"
                style={{
                  width: '100%',
                  maxHeight: 240,
                  objectFit: 'contain',
                  display: 'block',
                  backgroundColor: theme.gray?.[100] || '#F5F5F5',
                }}
              />
              {!processing && (
                <AppIconButton
                  icon={<XMarkIcon style={{ width: 18, height: 18 }} />}
                  tooltip="Remove image"
                  variant="ghost"
                  size="small"
                  onClick={resetState}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    backgroundColor: 'rgba(0,0,0,0.5) !important',
                    color: 'var(--text-inverse) !important',
                    '&:hover': { backgroundColor: 'rgba(0,0,0,0.7) !important' },
                  }}
                />
              )}
            </Box>

            {/* Processing steps */}
            {processing && (
              <Box sx={{ mb: 2 }}>
                <LinearProgress
                  sx={{
                    mb: 2,
                    borderRadius: 4,
                    backgroundColor: `${theme.primary}22`,
                    '& .MuiLinearProgress-bar': { backgroundColor: theme.primary },
                  }}
                />
              </Box>
            )}

            <Paper
              sx={{
                p: 2,
                borderRadius: '12px',
                backgroundColor: theme.components?.card?.background || '#FFFFFF',
                border: `1px solid ${theme.components?.card?.border || '#E0E0E0'}`,
                mb: 2.5,
              }}
            >
              {steps.map((step) => (
                <Box
                  key={step.key}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    py: 1,
                    opacity: step.status === 'pending' ? 0.4 : 1,
                    transition: 'opacity 0.2s',
                  }}
                >
                  {getStepIcon(step.status)}
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: step.status === 'active' ? 600 : 500,
                        color: step.status === 'error' ? (theme.danger || '#ef4444') : theme.text,
                      }}
                    >
                      {step.label}
                    </Typography>
                    {step.detail && (
                      <Typography variant="caption" sx={{ color: theme.gray?.[500] || '#888' }}>
                        {step.detail}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </Paper>

            {/* Error message */}
            {error && (
              <Paper
                sx={{
                  p: 2,
                  mb: 2,
                  borderRadius: '12px',
                  backgroundColor: `${theme.danger || '#ef4444'}10`,
                  border: `1px solid ${theme.danger || '#ef4444'}44`,
                }}
              >
                <Typography variant="body2" sx={{ color: theme.danger || '#ef4444', fontWeight: 500, mb: 1 }}>
                  {error}
                </Typography>
                <SecondaryButton
                  size="small"
                  onClick={() => {
                    handleClose();
                  }}
                >
                  Try Manual Entry
                </SecondaryButton>
              </Paper>
            )}

            {/* Action buttons */}
            {!processing && !error && (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <SecondaryButton
                  onClick={resetState}
                  sx={{ flex: 1 }}
                >
                  Retake
                </SecondaryButton>
                <PrimaryButton
                  onClick={processImage}
                  sx={{
                    flex: 2,
                    boxShadow: '0 2px 8px color-mix(in srgb, var(--action) 30%, transparent)',
                  }}
                >
                  Process Invoice
                </PrimaryButton>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Modal>
  );
};

// --- Utilities ---

function createImageElement(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = dataUrl;
  });
}

function dataURLtoBlob(dataUrl: string): Blob {
  const parts = dataUrl.split(',');
  const mime = parts[0].match(/:(.*?);/)?.[1] || 'image/jpeg';
  const bstr = atob(parts[1]);
  const n = bstr.length;
  const u8arr = new Uint8Array(n);
  for (let i = 0; i < n; i++) {
    u8arr[i] = bstr.charCodeAt(i);
  }
  return new Blob([u8arr], { type: mime });
}

export default InvoiceScanner;
